import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, Loader2, Smartphone } from "lucide-react";
import { pushSupported, isIOS, isStandalone, currentSubscription, enablePush, disablePush } from "@/lib/push";

export function PushToggleCard() {
  const supported = pushSupported();
  const iosNeedsInstall = isIOS() && !isStandalone();
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const denied = supported && typeof Notification !== "undefined" && Notification.permission === "denied";

  useEffect(() => {
    if (!supported) return;
    currentSubscription().then((s) => setOn(!!s)).catch(() => {});
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setOn(true);
      toast.success("Lead alerts enabled on this device");
    } catch (e) {
      const msg = e?.message === "denied"
        ? "Notifications are blocked. Allow them in your browser settings and try again."
        : e?.message === "not-configured"
          ? "Push isn't configured on the server yet."
          : "Couldn't enable notifications on this device.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try { await disablePush(); setOn(false); toast.success("Lead alerts turned off on this device"); }
    catch (e) { toast.error("Couldn't turn off notifications."); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6" data-testid="push-toggle-card">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><BellRing className="h-5 w-5 text-primary" /></div>
        <div>
          <h3 className="font-heading font-semibold">Lead alerts on this device</h3>
          <p className="text-xs text-muted-foreground">Get a push notification the moment a new lead is assigned or snapped to you — even when IShareLeads is closed.</p>
        </div>
      </div>

      {!supported ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="push-unsupported">This browser doesn't support push notifications. Try Chrome on Android or install the app on iPhone.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {iosNeedsInstall && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400" data-testid="push-ios-hint">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
              <span>On iPhone, first add IShareLeads to your Home Screen: tap the <strong>Share</strong> icon in Safari, then <strong>Add to Home Screen</strong>. Open it from there, then enable alerts.</span>
            </div>
          )}
          {denied && (
            <p className="text-sm text-destructive" data-testid="push-denied">Notifications are blocked for this site. Enable them in your browser's site settings, then try again.</p>
          )}
          {on ? (
            <Button variant="outline" onClick={disable} disabled={busy} data-testid="push-disable-button">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />} Turn off alerts on this device
            </Button>
          ) : (
            <Button onClick={enable} disabled={busy || denied} data-testid="push-enable-button">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BellRing className="mr-2 h-4 w-4" />} Enable lead alerts
            </Button>
          )}
          {on && <p className="text-xs text-muted-foreground" data-testid="push-on-note">Alerts are on for this device. You can enable it on each phone or computer you use.</p>}
        </div>
      )}
    </Card>
  );
}
